import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Settings, Mic, BookOpen, Users, Boxes, MessageCircle, Smartphone, ArrowRight } from "lucide-react";
import { getGuide } from "@/lib/userGuide";
import { useLang } from "@/lib/i18n";

export const GUIDE_ICONS = [Settings, Mic, BookOpen, Users, Boxes, MessageCircle, Smartphone];
export const GUIDE_SEEN_KEY = "kash_guide_seen";

function Card({ sec, i, ui }) {
  const Icon = GUIDE_ICONS[i];
  return (
    <div data-testid={`guide-card-${i}`} className="text-left">
      <div className="w-14 h-14 rounded-2xl bg-[#006D5B] text-white flex items-center justify-center mb-5 shadow-lg">
        <Icon size={26} />
      </div>
      <h3 className="font-heading font-bold text-2xl mb-2 leading-tight">{sec.t}</h3>
      <p className="text-gray-600 leading-relaxed mb-4">{sec.c}</p>
      {sec.e[0] && (
        <div className="bg-[#006D5B]/5 border border-[#006D5B]/15 rounded-xl px-3.5 py-2.5">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#006D5B]/70 mb-0.5">{ui.say}</p>
          <p className="text-sm font-medium text-[#063b32]">"{sec.e[0]}"</p>
        </div>
      )}
    </div>
  );
}

export default function GuideCards({ onClose }) {
  const { lang } = useLang();
  const G = getGuide(lang);
  const [idx, setIdx] = useState(0);
  const last = idx === G.sections.length - 1;
  const go = (d) => setIdx((v) => Math.min(Math.max(v + d, 0), G.sections.length - 1));
  const close = () => {
    localStorage.setItem(GUIDE_SEEN_KEY, "1");
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="guide-cards-modal" className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#FDFBF7] rounded-3xl w-full max-w-md p-7 relative overflow-hidden shadow-2xl">
        <button data-testid="guide-cards-close-btn" onClick={close} aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors duration-150">
          <X size={16} />
        </button>
        <p className="text-xs font-bold text-[#006D5B] mb-5">{G.ui.title} · {idx + 1}/{G.sections.length}</p>
        <div className="min-h-[290px]">
          <AnimatePresence mode="wait">
            <motion.div key={idx} drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.4}
              onDragEnd={(_, info) => info.offset.x < -60 ? go(1) : info.offset.x > 60 && go(-1)}
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.2 }}
              className="cursor-grab active:cursor-grabbing">
              <Card sec={G.sections[idx]} i={idx} ui={G.ui} />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-1.5 my-5">
          {G.sections.map((_, i) => (
            <button key={i} data-testid={`guide-dot-${i}`} onClick={() => setIdx(i)} aria-label={`Card ${i + 1}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-200 ${i === idx ? "w-6 bg-[#006D5B]" : "w-2 bg-black/15"}`} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          {last ? (
            <Link to="/guide" data-testid="guide-cards-full-link" onClick={close} className="text-sm font-semibold text-[#006D5B] hover:underline">{G.ui.full}</Link>
          ) : (
            <button data-testid="guide-cards-skip-btn" onClick={close} className="text-sm font-semibold text-gray-400 hover:text-gray-600">{G.ui.skip}</button>
          )}
          <button data-testid="guide-cards-next-btn" onClick={() => (last ? close() : go(1))}
            className="ml-auto inline-flex items-center gap-1.5 bg-[#006D5B] text-white text-sm font-bold px-5 py-3 rounded-full hover:-translate-y-0.5 transition-transform duration-150">
            {last ? G.ui.done : G.ui.next} <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
